/* eslint-disable camelcase */
import dayjs from 'dayjs';

const fmt = 'YYYY-MM-DD HH:mm:ssZ';

const toNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

export const getDiffByPercent = ({
  current,
  previous,
  fractionDigits = 2,
  epsilon = 1e-6,
  zeroPrevPolicy = 'signed100',
}) => {
  const c = toNumber(current, 0);
  const p = toNumber(previous, 0);

  if (Math.abs(p) < epsilon) {
    if (Math.abs(c) < epsilon) return 0;
    if (zeroPrevPolicy === 'zero') return 0;
    if (zeroPrevPolicy === 'dash') return 0;
    return c > 0 ? 100 : -100;
  }

  const res = (c / p - 1) * 100;
  if (!Number.isFinite(res)) return 0;

  return Number(res.toFixed(fractionDigits));
};

const normalizeDateUTC = value => {
  if (!value) return null;
  const d = dayjs.utc(value);
  if (!d.isValid()) return null;
  return d.startOf('day').format(fmt);
};

export const toFixedNumber = (v, fractionDigits = 2, fallback = 0) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Number(n.toFixed(fractionDigits));
};

export class StatisticApiConverter {
  convertStatistic = data => {
    const currTotalRaw = data?.total_metric_statistic?.current_period?.total;
    const prevTotalRaw = data?.total_metric_statistic?.previous_period?.total;

    const currentTotal = toFixedNumber(currTotalRaw, 2, 0);
    const previousTotal = toFixedNumber(prevTotalRaw, 2, 0);
    return {
      betCount: data.bet_count,
      activeUser: data.active_users_count,
      totalBets: data.total_bets,
      totalWin: data.total_win,
      totalNetwin: data.total_netwin,
      totalNetwinByDates: data?.netwin_by_dates?.map(t => ({
        date: t.date,
        count: t.netwin,
      })),
      totalMetricStatistic: {
        metricName: data?.total_metric_statistic?.metric_name,
        totalDiff: Number(data?.total_metric_statistic?.previous_period?.total) || 0,
        diffPercent: getDiffByPercent({
          current: data?.total_metric_statistic?.current_period?.total,
          previous: data?.total_metric_statistic?.previous_period?.total,
        }),
        currentPeriod: {
          total: currentTotal,
          totalByDate: data?.total_metric_statistic?.current_period?.total_by_date?.map(t => ({
            date: normalizeDateUTC(t.date),
            count: Number(t.value).toFixed(2),
          })),
        },
        previousPeriod: {
          total: previousTotal,
          totalByDate: data?.total_metric_statistic?.previous_period?.total_by_date?.map(t => ({
            date: normalizeDateUTC(t.date),
            count: Number(t.value).toFixed(2),
          })),
        },
      },
      totalUserBalance: data.total_user_balance,
      totalAgentBalance: data.total_agent_balance,
      totalMargin: isNaN((data?.total_netwin / data?.total_bets) * 100)
        ? 0
        : ((data?.total_netwin / data?.total_bets) * 100)?.toFixed(2),
      usersCount: data.users_count,
      directTotalNetwin: data.direct_total_netwin,
      rankedAgents: data.ranked_agents.map((t, i) => ({
        number: i + 1,
        id: t.user_id,
        username: t.username,
        netwin: t.netwin,
      })),
      rankedPlayers: data.ranked_players.map((t, i) => ({
        number: i + 1,
        id: t.user_id,
        username: t.username,
        netwin: t.netwin,
        bets: t.bets_sum,
        wins: t.wins_sum,
      })),
    };
  };

  convertBalanceStatistic = data => {
    const formatNumber = value => {
      return new Intl.NumberFormat('ru-RU', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true,
      }).format(value);
    };

    return data.items.map(item => ({
      currentAgent: item.current_agent,
      directAgent: item.direct_agent,
      startBalance: formatNumber(item.start_balance),
      endBalance: formatNumber(item.end_balance),
      changeBalance: formatNumber(item.balance_change),
      actualBalance: formatNumber(item.actual_balance),
      username: item.username,
      id: item.user_id,
      rank: item.rank,
    }));
  };
}

export default StatisticApiConverter;
